import { useState, useEffect } from 'react';
import PagesCleanerWidget from './PagesCleanerWidget';
import { FileText, Upload } from 'lucide-react';
import type { User } from '@supabase/supabase-js';

interface PagesCleanerWidgetClientProps {
  user: User | null;
  isGridLayout?: boolean;
}

/**
 * Client-only wrapper for PagesCleanerWidget with hydration-safe rendering
 * HYDRATION FIX: Always renders the same content on server and first client render
 * Only shows the actual component after hydration completes
 */
export default function PagesCleanerWidgetClient(props: PagesCleanerWidgetClientProps) {
  // HYDRATION FIX: Track if component has hydrated
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    // Render a static version during SSR that looks like the actual component
    return (
      <div className="bg-white dark:bg-charcoal-900 rounded-2xl p-8 shadow-lg border border-neutral-200 dark:border-charcoal-700/50 w-full h-full flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
            <FileText className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
              Clean Apple Pages Documents (.pages)
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Remove AI watermarks from Pages files while preserving all formatting
            </p>
          </div>
        </div>

        <div className="flex-1 border-2 border-dashed border-neutral-300 dark:border-charcoal-600 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-neutral-50 dark:bg-charcoal-800/50 min-h-[300px]">
          <Upload className="w-12 h-12 text-neutral-400 dark:text-neutral-500 mb-4" />
          <p className="text-neutral-700 dark:text-neutral-300 font-medium mb-2">
            Drop your Pages document here
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            or click to browse files
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">
            Only .pages files • Max 50MB
          </p>
        </div>

        <button
          disabled
          className="mt-4 w-full bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl opacity-50 cursor-not-allowed"
        >
          Clean Document & Download
        </button>

        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 text-center">
          All processing happens locally in your browser. Your document never leaves your device.
        </p>
      </div>
    );
  }

  // Render the actual component on the client
  return <PagesCleanerWidget {...props} />;
}
